import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrashIcon,
  PauseCircleIcon,
  ArrowsRightLeftIcon,
  LockClosedIcon,
  KeyIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Tenant } from './TenantTable';
import { apiClient } from '../../utils/simpleApiClient';

interface TenantActivity {
  id: string;
  type: 'login' | 'payment' | 'plan_change' | 'feature_usage' | 'support' | 'admin_action';
  description: string;
  timestamp: Date;
  user?: string;
  metadata?: Record<string, any>;
}

interface TenantUsage {
  period: string;
  apiCalls: number;
  storageUsed: number;
  activeUsers: number;
  features: Record<string, number>;
}

interface TenantDetails extends Tenant {
  billing_email: string;
  contact_phone: string;
  technical_contact: string;
  address: string;
  tax_id: string;
  subscription_tier: string;
  billing_cycle: string;
  next_billing_date: string;
  payment_method: string;
  credits: number;
  features: string[];
  usage_limits: {
    users: number;
    storage: number;
    api_calls: number;
  };
  activity: TenantActivity[];
  usage: TenantUsage[];
}

interface TenantProfileViewProps {
  isOpen: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onEdit: (tenant: Tenant) => void;
  onSuspend: (tenant: Tenant) => void;
  onDelete: (tenant: Tenant) => void;
  onManageSubscription: (tenant: Tenant) => void;
  onBillingAdjustment: (tenant: Tenant) => void;
  onMergeTenant?: (tenant: Tenant) => void;
}

const TenantProfileView: React.FC<TenantProfileViewProps> = ({
  isOpen,
  tenant,
  onClose,
  onEdit,
  onSuspend,
  onDelete,
  onManageSubscription,
  onBillingAdjustment,
  onMergeTenant
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tenantDetails, setTenantDetails] = useState<TenantDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant && isOpen) {
      fetchTenantDetails(tenant.id);
      fetchUserDetails(tenant.id);
    }
  }, [tenant, isOpen]);

  const fetchUserDetails = async (userId: string) => {
    try {
      const data = await apiClient.get(`/api/admin/users/${userId}`);
      setUserDetails(data.user);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleFieldUpdate = async (field: string, value: any) => {
    if (!userDetails) return;

    try {
      const data = await apiClient.put(`/api/admin/users/${userDetails.id}`, { [field]: value });
      setUserDetails(data.user);
      setEditingField(null);
      toast.success(`${field.replace(/_/g, ' ')} updated successfully`);
    } catch (error) {
      console.error('Failed to update field:', error);
      toast.error('Failed to update field');
    }
  };

  const handlePasswordUpdate = async () => {
    // Reset error state
    setPasswordError(null);
    
    // Validation
    if (!passwordForm.new_password || !passwordForm.confirm_password) {
      setPasswordError('Please fill in both password fields');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (!userDetails) return;
    
    setIsUpdatingPassword(true);
    
    try {
      await apiClient.put(`/api/admin/users/${userDetails.id}/password`, {
        password: passwordForm.new_password
      });

      // Clear the form
      setPasswordForm({
        new_password: '',
        confirm_password: ''
      });

      toast.success('Password updated successfully');
    } catch (error: any) {
      console.error('Failed to update password:', error);
      setPasswordError(error.message || 'Failed to update password');
      toast.error(error.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const fetchTenantDetails = async (tenantId: string) => {
    setIsLoading(true);
    try {
      // Simulate API call to fetch detailed tenant information
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockDetails: TenantDetails = {
        ...tenant!,
        billing_email: 'billing@' + tenant!.domain,
        contact_phone: '+1 (555) 123-4567',
        technical_contact: 'tech@' + tenant!.domain,
        address: '123 Business St, Suite 100, San Francisco, CA 94105',
        tax_id: 'TAX123456789',
        subscription_tier: 'professional',
        billing_cycle: 'monthly',
        next_billing_date: '2024-09-15',
        payment_method: '**** **** **** 1234',
        credits: 25.50,
        features: ['analytics', 'api_access', 'custom_branding'],
        usage_limits: {
          users: 25,
          storage: 100, // GB
          api_calls: 10000
        },
        activity: [
          {
            id: '1',
            type: 'login',
            description: 'Admin user logged in',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
            user: 'admin@' + tenant!.domain
          },
          {
            id: '2',
            type: 'payment',
            description: 'Monthly subscription payment processed',
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
            metadata: { amount: 99, currency: 'USD' }
          },
          {
            id: '3',
            type: 'feature_usage',
            description: 'API usage spike detected',
            timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            metadata: { api_calls: 8500 }
          }
        ],
        usage: [
          {
            period: '2024-08',
            apiCalls: 8500,
            storageUsed: 45.2,
            activeUsers: 18,
            features: { analytics: 150, api_access: 8500, custom_branding: 1 }
          },
          {
            period: '2024-07',
            apiCalls: 7200,
            storageUsed: 42.1,
            activeUsers: 16,
            features: { analytics: 120, api_access: 7200, custom_branding: 1 }
          }
        ]
      };
      
      setTenantDetails(mockDetails);
    } catch (error) {
      toast.error('Failed to load tenant details');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'billing', label: 'Billing' },
    { id: 'usage', label: 'Usage Analytics' },
    { id: 'activity', label: 'Activity Log' },
    { id: 'security', label: 'Security' },
    { id: 'settings', label: 'Settings' }
  ];

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'trial':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'past_due':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'cancelled':
        return 'text-gray-700 bg-gray-50 border-gray-200';
      case 'suspended':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  const renderEditableField = (field: string, label: string, value: any, type: string = 'text') => {
    const isEditing = editingField === field;
    
    return (
      <div className="flex items-start space-x-3 py-3 border-b border-gray-100 hover:bg-gray-50 rounded px-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          {isEditing ? (
            <input
              type={type}
              value={value || ''}
              onChange={(e) => setUserDetails({ ...userDetails, [field]: e.target.value })}
              onBlur={() => handleFieldUpdate(field, userDetails[field])}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFieldUpdate(field, userDetails[field]);
                }
              }}
              className="mt-1 text-sm text-gray-900 bg-white border border-blue-500 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <p 
              className="mt-1 text-sm text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => setEditingField(field)}
            >
              {value || <span className="text-gray-400 italic">Not set</span>}
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics - Keep the 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-700">User Role</p>
              <p className="text-2xl font-bold text-blue-900">{userDetails?.role || 'user'}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-700">Monthly Fee</p>
              <p className="text-2xl font-bold text-green-900">
                {formatCurrency(userDetails?.monthly_fee || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-700">Status</p>
              <p className="text-2xl font-bold text-purple-900">
                {userDetails?.status || 'active'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-orange-700">Last Login</p>
              <p className="text-sm font-bold text-orange-900">
                {userDetails?.last_login_at 
                  ? formatRelativeTime(new Date(userDetails.last_login_at))
                  : 'Never'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* User Information - All editable fields */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
        <div className="text-xs text-gray-500 mb-4">Click any field to edit</div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            {renderEditableField('first_name', 'First Name', userDetails?.first_name)}
            {renderEditableField('last_name', 'Last Name', userDetails?.last_name)}
            {renderEditableField('email', 'Email', userDetails?.email, 'email')}
            {renderEditableField('phone', 'Phone', userDetails?.phone, 'tel')}
            {renderEditableField('mobile_phone', 'Mobile Phone', userDetails?.mobile_phone, 'tel')}
            {renderEditableField('company_name', 'Company Name', userDetails?.company_name)}
            {renderEditableField('job_title', 'Job Title', userDetails?.job_title)}
            {renderEditableField('department', 'Department', userDetails?.department)}
          </div>
          
          <div>
            {renderEditableField('address', 'Address', userDetails?.address)}
            {renderEditableField('city', 'City', userDetails?.city)}
            {renderEditableField('state', 'State', userDetails?.state)}
            {renderEditableField('postal_code', 'Postal Code', userDetails?.postal_code)}
            {renderEditableField('country', 'Country', userDetails?.country)}
            {renderEditableField('timezone', 'Timezone', userDetails?.timezone)}
            {renderEditableField('language', 'Language', userDetails?.language)}
            {renderEditableField('bio', 'Bio', userDetails?.bio)}
          </div>
        </div>
      </div>

      {/* Social & Professional */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Social & Professional</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            {renderEditableField('linkedin_url', 'LinkedIn URL', userDetails?.linkedin_url, 'url')}
            {renderEditableField('twitter_handle', 'Twitter Handle', userDetails?.twitter_handle)}
          </div>
          <div>
            {renderEditableField('avatar_url', 'Avatar URL', userDetails?.avatar_url, 'url')}
            {renderEditableField('clerk_user_id', 'Clerk User ID', userDetails?.clerk_user_id)}
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            {renderEditableField('date_format', 'Date Format', userDetails?.date_format)}
            {renderEditableField('time_format', 'Time Format', userDetails?.time_format)}
          </div>
          <div>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 py-2">
                <input
                  type="checkbox"
                  checked={userDetails?.email_notifications || false}
                  onChange={(e) => handleFieldUpdate('email_notifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Email Notifications</span>
              </label>
              <label className="flex items-center space-x-3 py-2">
                <input
                  type="checkbox"
                  checked={userDetails?.sms_notifications || false}
                  onChange={(e) => handleFieldUpdate('sms_notifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">SMS Notifications</span>
              </label>
              <label className="flex items-center space-x-3 py-2">
                <input
                  type="checkbox"
                  checked={userDetails?.marketing_emails || false}
                  onChange={(e) => handleFieldUpdate('marketing_emails', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Marketing Emails</span>
              </label>
              <label className="flex items-center space-x-3 py-2">
                <input
                  type="checkbox"
                  checked={userDetails?.weekly_report || false}
                  onChange={(e) => handleFieldUpdate('weekly_report', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Weekly Report</span>
              </label>
              <label className="flex items-center space-x-3 py-2">
                <input
                  type="checkbox"
                  checked={userDetails?.instant_alerts || false}
                  onChange={(e) => handleFieldUpdate('instant_alerts', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Instant Alerts</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Privacy Settings</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 py-2">
            <input
              type="checkbox"
              checked={userDetails?.profile_public || false}
              onChange={(e) => handleFieldUpdate('profile_public', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Public Profile</span>
          </label>
          <label className="flex items-center space-x-3 py-2">
            <input
              type="checkbox"
              checked={userDetails?.show_email || false}
              onChange={(e) => handleFieldUpdate('show_email', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show Email</span>
          </label>
          <label className="flex items-center space-x-3 py-2">
            <input
              type="checkbox"
              checked={userDetails?.show_phone || false}
              onChange={(e) => handleFieldUpdate('show_phone', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show Phone</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      {/* Subscription Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-700">Plan</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">
              {tenantDetails?.subscription_tier}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Billing Cycle</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">
              {tenantDetails?.billing_cycle}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Next Billing Date</p>
            <p className="text-lg font-semibold text-gray-900">
              {tenantDetails?.next_billing_date ? formatDate(tenantDetails.next_billing_date) : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Monthly Revenue</p>
            <p className="text-lg font-semibold text-green-600">
              {formatCurrency(tenantDetails?.monthly_revenue || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-700">Payment Method</p>
            <p className="text-sm text-gray-900">{tenantDetails?.payment_method}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Account Credits</p>
            <p className="text-sm font-semibold text-green-600">
              {formatCurrency(tenantDetails?.credits || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => tenant && onBillingAdjustment(tenant)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Adjustment
          </button>
          <button
            onClick={() => tenant && onManageSubscription(tenant)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Manage Subscription
          </button>
          <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium">
            View Invoices
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium">
            Export Billing Data
          </button>
        </div>
      </div>
    </div>
  );

  const renderUsageTab = () => (
    <div className="space-y-6">
      {/* Usage Limits */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Usage & Limits</h3>
        <div className="space-y-6">
          {/* Users */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Users</span>
              <span className="text-sm text-gray-600">
                {tenantDetails?.users_count} / {tenantDetails?.usage_limits?.users}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{
                  width: `${Math.min(((tenantDetails?.users_count || 0) / (tenantDetails?.usage_limits?.users || 1)) * 100, 100)}%`
                }}
              ></div>
            </div>
          </div>

          {/* Storage */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Storage</span>
              <span className="text-sm text-gray-600">
                {tenantDetails?.usage?.[0]?.storageUsed || 0} GB / {tenantDetails?.usage_limits?.storage} GB
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{
                  width: `${Math.min(((tenantDetails?.usage?.[0]?.storageUsed || 0) / (tenantDetails?.usage_limits?.storage || 1)) * 100, 100)}%`
                }}
              ></div>
            </div>
          </div>

          {/* API Calls */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">API Calls (This Month)</span>
              <span className="text-sm text-gray-600">
                {tenantDetails?.usage?.[0]?.apiCalls.toLocaleString() || 0} / {tenantDetails?.usage_limits?.api_calls.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full"
                style={{
                  width: `${Math.min(((tenantDetails?.usage?.[0]?.apiCalls || 0) / (tenantDetails?.usage_limits?.api_calls || 1)) * 100, 100)}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Usage */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Feature Usage</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(tenantDetails?.usage?.[0]?.features || {}).map(([feature, usage]) => (
            <div key={feature} className="text-center">
              <p className="text-2xl font-bold text-gray-900">{usage.toLocaleString()}</p>
              <p className="text-sm text-gray-600 capitalize">{feature.replace('_', ' ')}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      {/* Password Change Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <LockClosedIcon className="h-6 w-6 text-gray-700 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              id="new_password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter new password (min. 8 characters)"
            />
          </div>
          
          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirm_password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Confirm new password"
            />
          </div>
          
          {passwordError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {passwordError}
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-gray-500">
              Password must be at least 8 characters long
            </div>
            <button
              onClick={handlePasswordUpdate}
              disabled={isUpdatingPassword || !passwordForm.new_password || !passwordForm.confirm_password}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isUpdatingPassword || !passwordForm.new_password || !passwordForm.confirm_password
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isUpdatingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      </div>

      {/* Account Security Status */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <ShieldCheckIcon className="h-6 w-6 text-gray-700 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Security Information</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Account Status</span>
            <span className={`text-sm font-medium ${
              userDetails?.status === 'active' ? 'text-green-600' : 
              userDetails?.status === 'suspended' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {userDetails?.status ? userDetails.status.charAt(0).toUpperCase() + userDetails.status.slice(1) : 'Unknown'}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Account Role</span>
            <span className="text-sm font-medium text-gray-900">
              {userDetails?.role || 'User'}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Last Login</span>
            <span className="text-sm text-gray-900">
              {userDetails?.last_login_at 
                ? formatRelativeTime(new Date(userDetails.last_login_at))
                : 'Never'
              }
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Account Created</span>
            <span className="text-sm text-gray-900">
              {userDetails?.created_at 
                ? formatDate(userDetails.created_at)
                : 'Unknown'
              }
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Two-Factor Authentication</span>
            <span className="text-sm text-gray-500">
              Not configured
            </span>
          </div>
        </div>
      </div>

      {/* Security Actions */}
      <div className="bg-white border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <KeyIcon className="h-6 w-6 text-yellow-700 mr-2" />
          <h3 className="text-lg font-medium text-yellow-900">Security Actions</h3>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => {
              if (confirm('Are you sure you want to force this user to reset their password on next login?')) {
                toast.success('Password reset will be required on next login');
              }
            }}
            className="w-full text-left p-3 border border-yellow-200 rounded-md hover:bg-yellow-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Force Password Reset</p>
                <p className="text-xs text-yellow-600">Require password change on next login</p>
              </div>
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            </div>
          </button>
          
          <button
            onClick={() => {
              if (confirm('Are you sure you want to revoke all active sessions for this user?')) {
                toast.success('All active sessions have been revoked');
              }
            }}
            className="w-full text-left p-3 border border-orange-200 rounded-md hover:bg-orange-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800">Revoke All Sessions</p>
                <p className="text-xs text-orange-600">Sign out user from all devices</p>
              </div>
              <LockClosedIcon className="h-5 w-5 text-orange-600" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {tenantDetails?.activity?.map((activity) => (
            <div key={activity.id} className="px-6 py-4">
              <div className="flex items-start space-x-3">
                <div className={`p-1 rounded-full ${
                  activity.type === 'login' ? 'bg-blue-100 text-blue-600' :
                  activity.type === 'payment' ? 'bg-green-100 text-green-600' :
                  activity.type === 'admin_action' ? 'bg-red-100 text-red-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {activity.type === 'login' && <UsersIcon className="h-4 w-4" />}
                  {activity.type === 'payment' && <CurrencyDollarIcon className="h-4 w-4" />}
                  {activity.type === 'feature_usage' && <ChartBarIcon className="h-4 w-4" />}
                  {activity.type === 'admin_action' && <Cog6ToothIcon className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <div className="mt-1 flex items-center space-x-2">
                    <p className="text-xs text-gray-500">
                      {formatRelativeTime(activity.timestamp)}
                    </p>
                    {activity.user && (
                      <>
                        <span className="text-xs text-gray-300">•</span>
                        <p className="text-xs text-gray-500">{activity.user}</p>
                      </>
                    )}
                  </div>
                  {activity.metadata && (
                    <div className="mt-2 text-xs text-gray-600">
                      {Object.entries(activity.metadata).map(([key, value]) => (
                        <span key={key} className="mr-3">
                          {key}: {String(value)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* Features */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Enabled Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tenantDetails?.features?.map((feature) => (
            <div key={feature} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
              <span className="text-sm font-medium text-green-800 capitalize">
                {feature.replace('_', ' ')}
              </span>
              <CheckCircleIcon className="h-4 w-4 text-green-600" />
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Actions */}
      {onMergeTenant && (
        <div className="bg-white border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">Advanced Actions</h3>
          <button
            onClick={() => tenant && onMergeTenant(tenant)}
            className="w-full text-left p-3 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Merge Tenant</p>
                <p className="text-xs text-blue-600">Combine with another tenant account</p>
              </div>
              <ArrowsRightLeftIcon className="h-5 w-5 text-blue-600" />
            </div>
          </button>
        </div>
      )}

      {/* Danger Zone */}
      <div className="bg-white border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-900 mb-4 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
          Danger Zone
        </h3>
        <div className="space-y-3">
          <button
            onClick={() => tenant && onSuspend(tenant)}
            className="w-full text-left p-3 border border-yellow-200 rounded-md hover:bg-yellow-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Suspend Tenant</p>
                <p className="text-xs text-yellow-600">Temporarily disable access</p>
              </div>
              <PauseCircleIcon className="h-5 w-5 text-yellow-600" />
            </div>
          </button>
          
          <button
            onClick={() => tenant && onDelete(tenant)}
            className="w-full text-left p-3 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Delete Tenant</p>
                <p className="text-xs text-red-600">Permanently remove account and data</p>
              </div>
              <TrashIcon className="h-5 w-5 text-red-600" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  if (!isOpen || !tenant) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{tenant.name}</h3>
                  <p className="text-sm text-gray-500">{tenant.domain}</p>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(tenant.subscription_status)}`}>
                  {tenant.subscription_status}
                </span>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="px-6 flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading tenant details...</p>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'billing' && renderBillingTab()}
                {activeTab === 'usage' && renderUsageTab()}
                {activeTab === 'activity' && renderActivityTab()}
                {activeTab === 'security' && renderSecurityTab()}
                {activeTab === 'settings' && renderSettingsTab()}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Close
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={() => tenant && onEdit(tenant)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Edit Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantProfileView;